import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, ExternalLink, Calendar, MapPin } from "lucide-react";
import { getCompanyBySlug, getRelatedCompanies } from "../mock";

const CompanyDetail = () => {
  const { slug } = useParams();
  const company = getCompanyBySlug(slug);
  const relatedCompanies = getRelatedCompanies(slug);

  if (!company) {
    return <Navigate to="/portfolio" replace />;
  }

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <Link to="/portfolio">
          <Button
            variant="outline"
            className="border-slate-300 text-slate-900 hover:bg-slate-100"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back to Portfolio
          </Button>
        </Link>
      </div>

      {/* Company Header */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-1 bg-orange-100 text-orange-700 text-sm font-semibold rounded-full uppercase tracking-wide">
                {company.category}
              </span>
              <span
                className={`px-4 py-1 rounded-full text-sm font-semibold ${
                  company.status === "Live"
                    ? "bg-green-100 text-green-700"
                    : company.status === "Development"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {company.status}
              </span>
            </div>

            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              {company.name}
            </h1>

            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {company.description}
            </p>

            {/* Company Info */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 mr-3 text-orange-600 mt-1" />
                <div>
                  <p className="text-sm text-slate-500 mb-1">Founded</p>
                  <p className="font-semibold text-slate-900">
                    {company.yearFounded}
                  </p>
                </div>
              </div>
              {/* <div className="flex items-start">
                <MapPin className="w-5 h-5 mr-3 text-orange-600 mt-1" />
                <div>
                  <p className="text-sm text-slate-500 mb-1">Location</p>
                  <p className="font-semibold text-slate-900">
                    {company.location}
                  </p>
                </div>
              </div> */}
            </div>

            <div className="flex gap-4">
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                  Visit Website <ExternalLink className="ml-2" size={18} />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-900 hover:bg-slate-100"
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={company.image}
                alt={company.name}
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Related Companies */}
      {relatedCompanies.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 mt-20">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">
            Related Portfolio Companies
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {relatedCompanies.map((related) => (
              <Link
                key={related.id}
                to={`/portfolio/${related.slug}`}
                className="group"
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-orange-600 font-semibold mb-2">
                      {related.category}
                    </p>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      {related.name}
                    </h3>
                    <p className="text-slate-600">{related.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
